/**
 * Google Tasks list scope binding — pure routing helpers (no I/O).
 * Each enabled Google list maps to (organization_id, optional project_id OR client_id).
 */

import type { GoogleTask } from '@/types/google-tasks';
import type { Todo } from '@flowstate/types';

export interface ListScopeBinding {
  google_task_list_id: string;
  organization_id: string | null;
  project_id?: string | null;
  client_id?: string | null;
  sync_enabled: boolean;
  is_primary?: boolean | null;
}

export interface EnableListScopeInput {
  organizationId: string;
  projectId?: string | null;
  clientId?: string | null;
}

export class GoogleTasksScopeError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'GoogleTasksScopeError';
  }
}

/** Normalize nullable FK for comparison */
function normId(id: string | null | undefined): string | null {
  return id ?? null;
}

export function validateEnableListScope(input: EnableListScopeInput): void {
  if (!input.organizationId?.trim()) {
    throw new GoogleTasksScopeError('Organization is required to enable Google Tasks sync');
  }
  if (input.projectId && input.clientId) {
    throw new GoogleTasksScopeError('A list cannot bind to both a project and a client');
  }
}

/** Whether a todo's scope exactly matches a list binding (null-safe FK equality). */
export function todoMatchesBinding(todo: Pick<Todo, 'organization_id' | 'project_id' | 'client_id'>, binding: ListScopeBinding): boolean {
  if (!binding.sync_enabled || !binding.organization_id) {
    return false;
  }
  if (normId(todo.organization_id) !== normId(binding.organization_id)) {
    return false;
  }
  if (normId(todo.project_id) !== normId(binding.project_id)) {
    return false;
  }
  if (normId(todo.client_id) !== normId(binding.client_id)) {
    return false;
  }
  return true;
}

function bindingSpecificity(binding: ListScopeBinding): number {
  if (binding.project_id || binding.client_id) {
    return 2;
  }
  return 1;
}

/**
 * Pick target Google list for export.
 * Priority: existing mapping list → exact scope → org+project (project only) → org-only → is_primary for org.
 */
export function resolveExportListId(
  todo: Pick<Todo, 'organization_id' | 'project_id' | 'client_id'>,
  bindings: ListScopeBinding[],
  existingListId?: string | null,
): string {
  const enabled = bindings.filter((b) => b.sync_enabled && b.organization_id);
  if (enabled.length === 0) {
    throw new GoogleTasksScopeError('No Google Task lists enabled for sync');
  }

  if (existingListId) {
    const mapped = enabled.find((b) => b.google_task_list_id === existingListId);
    if (mapped) {
      return existingListId;
    }
  }

  const exact = enabled.filter((b) => todoMatchesBinding(todo, b));
  if (exact.length >= 1) {
    exact.sort((a, b) => bindingSpecificity(b) - bindingSpecificity(a));
    const topSpec = bindingSpecificity(exact[0]);
    const sameSpec = exact.filter((b) => bindingSpecificity(b) === topSpec);
    if (sameSpec.length > 1) {
      const primary = sameSpec.find((b) => b.is_primary);
      if (primary) {
        return primary.google_task_list_id;
      }
    }
    return exact[0].google_task_list_id;
  }

  const orgId = normId(todo.organization_id);
  if (!orgId) {
    throw new GoogleTasksScopeError('Todo has no organization; configure a Google list scope first');
  }

  const projectId = normId(todo.project_id);
  if (projectId) {
    const projectMatch = enabled.find(
      (b) =>
        b.organization_id === orgId &&
        normId(b.project_id) === projectId &&
        !normId(b.client_id),
    );
    if (projectMatch) {
      return projectMatch.google_task_list_id;
    }
  }

  const clientId = normId(todo.client_id);
  if (clientId) {
    const clientMatch = enabled.find(
      (b) =>
        b.organization_id === orgId &&
        normId(b.client_id) === clientId &&
        !normId(b.project_id),
    );
    if (clientMatch) {
      return clientMatch.google_task_list_id;
    }
  }

  const orgOnly = enabled.filter(
    (b) => b.organization_id === orgId && !normId(b.project_id) && !normId(b.client_id),
  );
  if (orgOnly.length === 1) {
    return orgOnly[0].google_task_list_id;
  }
  if (orgOnly.length > 1) {
    const primary = orgOnly.find((b) => b.is_primary);
    if (primary) {
      return primary.google_task_list_id;
    }
    return orgOnly[0].google_task_list_id;
  }

  const primaryForOrg = enabled.find((b) => b.organization_id === orgId && b.is_primary);
  if (primaryForOrg) {
    return primaryForOrg.google_task_list_id;
  }

  throw new GoogleTasksScopeError(
    `No Google Task list configured for organization scope (org=${orgId}, project=${projectId ?? 'none'}, client=${clientId ?? 'none'})`,
  );
}

/** Whether todo scope matches any enabled list binding (for export eligibility). */
export function todoHasExportableScope(
  todo: Pick<Todo, 'organization_id' | 'project_id' | 'client_id'>,
  bindings: ListScopeBinding[],
): boolean {
  const enabled = bindings.filter((b) => b.sync_enabled && b.organization_id);
  if (enabled.length === 0) {
    return false;
  }
  try {
    resolveExportListId(todo, bindings);
    return true;
  } catch {
    return false;
  }
}

/**
 * Sort Google tasks so parents are processed before children (topological).
 * Tasks without parents or with unknown parents come first among roots.
 */
export function sortGoogleTasksParentsFirst(tasks: GoogleTask[]): GoogleTask[] {
  const byId = new Map(tasks.map((t) => [t.id, t]));
  const visited = new Set<string>();
  const result: GoogleTask[] = [];

  const visit = (task: GoogleTask) => {
    if (visited.has(task.id)) {
      return;
    }
    if (task.parent && byId.has(task.parent) && !visited.has(task.parent)) {
      visit(byId.get(task.parent)!);
    }
    visited.add(task.id);
    result.push(task);
  };

  for (const task of tasks) {
    visit(task);
  }

  return result;
}
